import { reportError } from './ErrorReporter';

// nginx client_max_body_size is 500M; leave 1MiB for multipart wrapping on a single-file POST.
export const MAX_UPLOAD_FILE_BYTES = 499 * 1024 * 1024;

export function isFileTooLarge(file) {
    return file.size > MAX_UPLOAD_FILE_BYTES;
}

export function getTooLargeUploadMessage(fileNames) {
    if (fileNames.length === 1) {
        return `"${fileNames[0]}" was not uploaded because it is too large.`;
    }
    return `${fileNames.length} files were not uploaded because they are too large.`;
}

export function getFailedUploadMessage(fileNames) {
    if (fileNames.length === 1) {
        return `Failed to upload "${fileNames[0]}". Please try again`;
    }
    return `Failed to upload ${fileNames.length} documents. Please try again`;
}

/**
 * Snackbar text for a sequential upload batch. Returns null if nothing failed.
 * Too-large files are listed first; other failures second. One file → name; several → count.
 */
export function getDocumentUploadOutcomeMessage({ tooLargeNames = [], failedNames = [] } = {}) {
    const parts = [];
    if (tooLargeNames.length > 0) {
        parts.push(getTooLargeUploadMessage(tooLargeNames));
    }
    if (failedNames.length > 0) {
        parts.push(getFailedUploadMessage(failedNames));
    }
    if (parts.length === 0) {
        return null;
    }
    return 'ERROR: ' + parts.join(' ');
}

export function revertUploadEntries(currentMap, currentUploading, names, previousMap, previousUploading) {
    const nextMap = { ...currentMap };
    const nextUploading = { ...currentUploading };
    for (const name of names) {
        if (Object.prototype.hasOwnProperty.call(previousMap, name)) {
            nextMap[name] = previousMap[name];
        } else {
            delete nextMap[name];
        }
        if (Object.prototype.hasOwnProperty.call(previousUploading, name)) {
            nextUploading[name] = previousUploading[name];
        } else {
            delete nextUploading[name];
        }
    }
    return { nextMap, nextUploading };
}

/**
 * Reports a failed document upload to ErrorReporter.
 * Used by Case.onFilesSelectedForUpload for network and HTTP failures.
 */
export async function reportDocumentUploadFailure({
    error,
    caseId,
    fileCount,
    fileNames,
    status,
    responseBody,
}) {
    const err = error instanceof Error ? error : new Error(String(error));
    const additionalData = {
        caseId,
        fileCount,
        fileNames,
    };
    if (status !== undefined) {
        additionalData.status = status;
    }
    if (responseBody !== undefined) {
        additionalData.responseBody = responseBody;
    }
    await reportError(err, 'Case.onFilesSelectedForUpload', additionalData);
}

